// Unpublished Work © 2023-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {Redirect, Route, Switch} from 'react-router-dom';
import Helmet from 'react-helmet';
import EntryItem from 'Ui/components/entry/entry-item';
import Equipment from 'OnEquip/equipment/equipment';
import EquipmentReport from 'Ui/pages/onequip/reports/equipment-report';
import Footer from 'Ui/components/footer/footer';
import Labor from 'OnLabor/labor';
import Parts from 'Ui/pages/onequip/parts/parts';
import {TertiaryNavBarWrapper} from 'Ui/components/tertiary-navbar';
import WorkboardPresentationWrapper from 'Ui/pages/onlabor/workboards/presentation/presentation-wrapper';
import {AuthContextProvider} from 'Ui/context/auth-context';
import {
    ANALYZE_FLEET_TITLE,
    ANALYZE_OPERATIONS_TITLE,
    FLEET_MAINTENANCE_TITLE,
    HOME_TITLE,
    LABOR_TITLE,
    ONLABOR,
    ONLINK_DASHBOARD,
    PARTS_TITLE,
    WORKBOARD
} from 'Ui/constants/tab-header-constants';
import appAccess from 'Common/constants/app-access';
import {
    ANALYZE_COURSE_OPERATIONS,
    DRILL_IN,
    DRILL_IN_CHART,
    EQUIPMENT_REPORT,
    FORETEES,
    HOME,
    MONTHLY_LABOR_REPORT,
    MY_WORK,
    PLAYBOOKS,
    TWC,
    WORKBOARD_PRESENTATION
} from 'Common/constants/routes';
import {BASE_ROUTES, getBaseRouteConfig} from 'Ui/constants/routes/base-route-config';
import {clsx} from 'clsx';
import {useDeepMemo} from 'Utils/react-utils';
import {
    VIEW_EQUIPMENT_LIST_API,
    VIEW_EQUIPMENT_DETAILS,
    VIEW_PARTS,
    VIEW_FINANCIAL_DATA
} from 'Common/constants/business-activities';
import {
    PRO_GOLF_HOME,
    TOOLS_COURSE_OPERATIONS,
    TOOLS_FLEET_MAINTENANCE,
    TOOLS_FLEET_PARTS,
    TOOLS_LABOR
} from 'Common/constants/turf-features';

function getRoutes(translations, isMigrated) {
    const analyzeTabHeader = isMigrated ? translations[ANALYZE_OPERATIONS_TITLE] : translations[ANALYZE_FLEET_TITLE];

    return [
        {
            ...BASE_ROUTES[MONTHLY_LABOR_REPORT],
            requiredFeature: TOOLS_COURSE_OPERATIONS,
            tabHeader: translations[ANALYZE_OPERATIONS_TITLE]
        },
        {
            ...getBaseRouteConfig(ANALYZE_COURSE_OPERATIONS, isMigrated),
            requiredFeature: TOOLS_COURSE_OPERATIONS,
            tabHeader: translations[ANALYZE_OPERATIONS_TITLE]
        },
        {
            ...BASE_ROUTES[HOME],
            requiredFeature: PRO_GOLF_HOME,
            tabHeader: translations[HOME_TITLE]
        },
        {
            ...BASE_ROUTES[DRILL_IN],
            tabHeader: translations[HOME_TITLE]
        },
        {
            ...BASE_ROUTES[PLAYBOOKS]
        },
        {
            ...BASE_ROUTES[TWC]
        },
        {
            ...BASE_ROUTES[FORETEES]
        },
        {
            ...BASE_ROUTES[DRILL_IN_CHART]
        },
        {
            appAccess: appAccess.ONEQUIP,
            component: EquipmentReport,
            path: EQUIPMENT_REPORT,
            tabHeader: analyzeTabHeader,
            myJdPermissions: [VIEW_EQUIPMENT_DETAILS, VIEW_FINANCIAL_DATA]
        },
        {
            appAccess: appAccess.ONEQUIP,
            className: isMigrated ? 'migrated-parts-container' : 'parts-container',
            component: Parts,
            path: '/equipment/parts/:type',
            requiredFeature: TOOLS_FLEET_PARTS,
            tabHeader: isMigrated ? translations[PARTS_TITLE] : translations[FLEET_MAINTENANCE_TITLE],
            paramsAuth: {
                type: {
                    history: {
                        migratedMembershipAccessOnly: true,
                        myJdPermissions: [VIEW_PARTS]
                    },
                    inventory: {
                        myJdPermissions: [VIEW_PARTS]
                    },
                    orders: {
                        myJdPermissions: [VIEW_PARTS]
                    },
                    recommended: {
                        myJdPermissions: [VIEW_PARTS]
                    }
                }
            }
        },
        {
            appAccess: appAccess.ONEQUIP,
            className: 'onequip-container',
            component: Equipment,
            path: '/equipment/:type',
            requiredFeature: TOOLS_FLEET_MAINTENANCE,
            tabHeader: translations[FLEET_MAINTENANCE_TITLE],
            paramsAuth: {
                type: {
                    fleet: {
                        myJdPermissions: [VIEW_EQUIPMENT_LIST_API]
                    },
                    hoc: {
                        myJdPermissions: [VIEW_EQUIPMENT_LIST_API]
                    },
                    suggestedMaintenance: {
                        myJdPermissions: [VIEW_EQUIPMENT_LIST_API]
                    },
                    shop: {
                        myJdPermissions: [VIEW_EQUIPMENT_DETAILS]
                    },
                    assignments: {
                        myJdPermissions: [VIEW_EQUIPMENT_DETAILS]
                    },
                    serviceHistory: {
                        myJdPermissions: [VIEW_EQUIPMENT_LIST_API]
                    }
                }
            }
        },
        {
            ...BASE_ROUTES[MY_WORK],
            className: 'my-work-container',
            exact: true,
            tabHeader: translations[LABOR_TITLE]
        },
        {
            appAccess: appAccess.ONLABOR,
            component: WorkboardPresentationWrapper,
            exact: true,
            hideFooter: true,
            isStandalonePage: true,
            path: WORKBOARD_PRESENTATION,
            tabHeader: isMigrated ? WORKBOARD : ONLABOR
        },
        {
            appAccess: appAccess.ONLABOR,
            component: Labor,
            path: '/labor',
            requiredFeature: TOOLS_LABOR,
            tabHeader: translations[LABOR_TITLE]
        }
    ];
}

function createRoutes({
    footer,
    hasSubscription,
    isMigrated,
    licensedFeatures,
    loadPersistentHeader,
    translations
}) {
    const routes = getRoutes(translations, isMigrated);

    return routes.map(({
        appAccess,
        className,
        component,
        hideFooter,
        myJdPermissions,
        paramsAuth,
        permission,
        requiredFeature,
        toggle,
        ...routeProps
    }) => (
        <Route
            {...routeProps}
            key={routeProps.path}
        >
            <Helmet>
                <title>{routeProps.tabHeader || ONLINK_DASHBOARD}</title>
            </Helmet>
            <EntryItem
                className={className}
                component={component}
                footer={!hideFooter && footer}
                hasSubscription={hasSubscription}
                licensedFeatures={licensedFeatures}
                loadPersistentHeader={loadPersistentHeader}
                paramsAuth={paramsAuth}
                requiredAuth={{
                    appAccess,
                    myJdPermissions,
                    permission,
                    requiredFeature,
                    toggle
                }}
                translations={translations}
            />
        </Route>
    ));
}

function Entry(props) {
    const {
        hasSubscription,
        isMigrated,
        isStandalonePage,
        isWorkboardForm,
        licensedFeatures,
        loadPersistentHeader,
        translations
    } = props;

    const mainContentClassName = React.useMemo(() => clsx('main-content', 'jd-green', {
        'ph-redesign-content': !loadPersistentHeader,
        'ph-integration-content': loadPersistentHeader
    }), [loadPersistentHeader]);
    const footer = useDeepMemo(() => <Footer translations={translations}/>, [translations]);

    return (
        <AuthContextProvider>
            <div className={isStandalonePage ? 'standalone-content' : mainContentClassName}>
                {
                    !isStandalonePage && !isWorkboardForm &&
                    <TertiaryNavBarWrapper
                        hasSubscription={hasSubscription}
                        isMigrated={isMigrated}
                    />
                }
                <Switch>
                    <Redirect
                        exact={true}
                        from='/'
                        to='/home'
                    />
                    <Redirect
                        exact={true}
                        from='/equipment'
                        to='/equipment/fleet'
                    />
                    <Redirect
                        exact={true}
                        from='/equipment/parts'
                        to='/equipment/parts/inventory'
                    />
                    <Redirect
                        exact={true}
                        from='/labor'
                        to='/labor/workboard'
                    />
                    <Redirect
                        exact={true}
                        from='/labor/report/monthly'
                        to='/labor/report/monthly/jobCategories'
                    />
                    {
                        createRoutes({
                            footer,
                            hasSubscription,
                            isMigrated,
                            licensedFeatures,
                            loadPersistentHeader,
                            translations
                        })
                    }
                </Switch>
            </div>
        </AuthContextProvider>
    );
}

Entry.propTypes = {
    hasSubscription: PropTypes.bool,
    isMigrated: PropTypes.bool,
    isStandalonePage: PropTypes.bool,
    isWorkboardForm: PropTypes.bool,
    licensedFeatures: PropTypes.arrayOf(PropTypes.string),
    loadPersistentHeader: PropTypes.bool,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        isMigrated: state.membership.isMigrated
    };
}

export default connect(mapStateToProps)(Entry);
